import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

/**
 * RoundWinSounds — the CS:GO-style end-of-round announcer (2D, both ears):
 *   • "Counter-Terrorists Win" — the CT side took the round
 *   • "Terrorists Win"          — the T side took the round
 *
 * Played to BOTH players on every `round_end` (including the match-deciding
 * round), chosen by the broadcast's `winnerTeam`, which the server resolves
 * post-halftime so it always tracks whoever is currently CT / T. A non-
 * positional one-shot — it's an announcer, not a world sound.
 */

const CT_WIN_URL = "/audio/ct-win.mp3";
const T_WIN_URL = "/audio/t-win.mp3";
const ALL_URLS = [CT_WIN_URL, T_WIN_URL];
const VOLUME = 0.7;

const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

export class RoundWinSounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly emitter: pc.Entity;
  private readonly assets = new Map<string, pc.Asset>();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(app: pc.AppBase, client: GameClient) {
    this.app = app;
    this.client = client;

    this.emitter = new pc.Entity("round-win-snd");
    this.app.root.addChild(this.emitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    this.unsubs.push(
      this.client.onRoundEnd((msg) => {
        this.play(msg.winnerTeam === "t" ? T_WIN_URL : CT_WIN_URL);
      }),
    );
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.emitter.destroy();
  }

  private play(url: string): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    this.emitter.sound?.play(slotName(url));
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) console.warn("[RoundWinSounds] failed to load:", url, err);
        else this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    this.emitter.addComponent("sound");
    const s = this.emitter.sound;
    if (s) {
      s.positional = false; // 2D — announcer
      for (const url of ALL_URLS) {
        const asset = this.assets.get(url);
        if (asset) {
          s.addSlot(slotName(url), { asset: asset.id, volume: VOLUME, loop: false, autoPlay: false, overlap: false });
        }
      }
    }
    this.ready = true;
  }
}
